import { ActionButton } from "@via/components/action-button/action-button.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import { getAppStatusLabel } from "@via/pages/projects/runs/utils/helpers.tsx";
import { AppsActions, MY_APPS_TABLE_OPTIONS } from "@via/utils/constants.tsx";
import { type TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { type AppListResponseBodyItem, ContainerStatus } from "common";
import { type JSX } from "react";

/**
 * Why Launch is disabled for a version the apps cluster has not taken (#953). Someone who can manage
 * the app has Retry deploy in the same menu; anyone else has to ask the owner.
 */
export const NOT_DEPLOYED_LAUNCH_HINT_MANAGER =
  "This version is not deployed yet. Use Retry deploy to deploy it.";
export const NOT_DEPLOYED_LAUNCH_HINT_OTHERS =
  "This version is not deployed yet. Ask the app's owner to retry the deploy.";

/** Who may edit, remove and redeploy an app: an admin, or its owner with app modify rights. */
export const canManageApp = (
  app: Pick<AppListResponseBodyItem, "own">,
  user: { isAdmin: boolean; canModifyApp: boolean }
): boolean => user.isAdmin || (app.own && user.canModifyApp);

/** The manager's menu. A not_deployed version gets Retry deploy, and Launch disabled with the reason. */
export const appMenuOptions = (
  notDeployed: boolean,
  redeploying = false
): TableContextMenuInterface[] =>
  MY_APPS_TABLE_OPTIONS.flatMap((option): TableContextMenuInterface[] => {
    if (option.name !== AppsActions.Launch || !notDeployed) {
      return [option];
    }
    return [
      { ...option, disabled: true, title: NOT_DEPLOYED_LAUNCH_HINT_MANAGER },
      { name: AppsActions.Redeploy, href: "#", loading: redeploying },
    ];
  });

export const AppDeployBadge = ({
  status,
}: {
  status: ContainerStatus;
}): JSX.Element | null =>
  status === ContainerStatus.NotDeployed ? (
    <span className="ml-2 shrink-0">{getAppStatusLabel(status)}</span>
  ) : null;

/** The Options cell of a My Apps row. */
export const AppRowActions = ({
  app,
  isAdmin,
  canModifyApp,
  redeploying = false,
  onAction,
}: {
  app: AppListResponseBodyItem;
  isAdmin: boolean;
  canModifyApp: boolean;
  redeploying?: boolean;
  onAction: (name: string, app: AppListResponseBodyItem) => void;
}): JSX.Element => {
  if (app.status === ContainerStatus.Creating) {
    return (
      <div className="flex max-w-12 flex-row items-center">
        <Spinner spinColor={"rgb(128,128,128)"} loading={true} />
        <span className="pl-1">Initializing</span>
      </div>
    );
  }
  const notDeployed = app.status === ContainerStatus.NotDeployed;
  if (canManageApp(app, { isAdmin, canModifyApp })) {
    return (
      <TableContextMenu
        options={appMenuOptions(notDeployed, redeploying)}
        onSelect={option => onAction(option.name, app)}
      />
    );
  }
  return (
    <ActionButton
      button={AppsActions.Launch}
      primary={true}
      showIcon={false}
      disabled={notDeployed}
      title={notDeployed ? NOT_DEPLOYED_LAUNCH_HINT_OTHERS : undefined}
      className="shrink-0 whitespace-nowrap"
      onClick={() => onAction(AppsActions.Launch, app)}
    />
  );
};
